// User API functions
import { getUserInfo } from './auth';
import { CommonUtils } from './commonUtils';
import { getApiV1Url } from '../config/api';

// ============================================================================
// INTERFACES
// ============================================================================

// 기본 사용자 정보
export interface User {
  userId: string;
  name: string;
  status: string;
  department: string;
  createdAt: string;
}

// 사용자 목록 조회 요청
export interface UserListRequest {
  page?: number;
  size?: number;
  sort?: string;
  status?: string;
  search?: string;
}

// 사용자 목록 조회 응답
export interface UserListResponse {
  totalCount: number;
  users: User[];
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// 사용자 생성 요청
export interface UserCreateRequest {
  userId: string;
  password: string;
  status: string;
  name: string;
  mfaType?: string;
  department?: string;
  phoneNumber?: string;
}

// 사용자 생성 응답
export interface UserCreateResponse {
  success: boolean;
  message: string;
  data?: any;
}

// 사용자 상세 정보
export interface UserDetail {
  userId: string;
  name: string;
  status: string;
  mfaType: string;
  department: string;
  phoneNumber: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

// 사용자 수정 요청
export interface UserUpdateRequest {
  userId: string;
  password?: string;
  status: string;
  name: string;
  mfaType?: string;
  department?: string;
  phoneNumber?: string;
}

// 사용자 수정 응답
export interface UserUpdateResponse {
  success: boolean;
  message: string;
  data?: any;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

// 사용자 목록 조회 API
export const getUserList = async (
  accountCode: string,
  request: UserListRequest = {}
): Promise<UserListResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const queryParams = new URLSearchParams();
    if (request.page !== undefined) queryParams.append('page', request.page.toString());
    if (request.size !== undefined) queryParams.append('size', request.size.toString());
    if (request.sort) queryParams.append('sort', request.sort);
    if (request.status) queryParams.append('status', request.status);
    if (request.search) queryParams.append('search', request.search);

    const response = await fetch(
      `${getApiV1Url(`/accounts/${accountCode}/users`)}?${queryParams}`,
      {
        method: 'GET',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('사용자 목록 API 응답:', result);
    
    if (result.data) {
      return {
        totalCount: result.data.totalCount || 0,
        users: result.data.users || [],
        totalPages: result.data.totalPages || 0,
        currentPage: result.data.currentPage || 0,
        pageSize: result.data.pageSize || 20,
        hasNext: result.data.hasNext || false,
        hasPrevious: result.data.hasPrevious || false
      };
    }
    
    return {
      totalCount: 0,
      users: [],
      totalPages: 0,
      currentPage: 0,
      pageSize: 20,
      hasNext: false,
      hasPrevious: false
    };
  } catch (error: any) {
    console.error('사용자 목록 조회 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 사용자 생성 API
export const createUser = async (
  accountCode: string,
  userData: UserCreateRequest
): Promise<UserCreateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/accounts/${accountCode}/users`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(userData)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // 서버 에러 응답 구조에 맞게 에러 메시지 처리
      if (errorData.errors && errorData.errors.error && Array.isArray(errorData.errors.error)) {
        const errorMessages = errorData.errors.error.map((err: any) => err.message).join(', ');
        throw new Error(errorMessages);
      }
      
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('사용자 생성 API 응답:', result);
    
    return {
      success: true,
      message: result.message || '사용자가 성공적으로 생성되었습니다.',
      data: result.data || result
    };
  } catch (error: any) {
    console.error('사용자 생성 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 사용자 상세 정보 조회 API
export const getUserDetail = async (
  accountCode: string,
  userId: string
): Promise<UserDetail> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/accounts/${accountCode}/users/${userId}`),
      {
        method: 'GET',
        headers
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('사용자 상세 정보 API 응답:', result);
    
    if (result.data) {
      return {
        userId: result.data.userId,
        name: result.data.name,
        status: result.data.status,
        mfaType: result.data.mfaType || 'NONE',
        department: result.data.department || '',
        phoneNumber: result.data.phoneNumber || '',
        createdAt: result.data.createdAt,
        createdBy: result.data.createdBy || '',
        updatedAt: result.data.updatedAt,
        updatedBy: result.data.updatedBy || ''
      };
    }
    
    throw new Error('사용자 정보를 찾을 수 없습니다.');
  } catch (error: any) {
    console.error('사용자 상세 정보 조회 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 사용자 수정 API
export const updateUser = async (
  accountCode: string,
  userId: string,
  userData: UserUpdateRequest
): Promise<UserUpdateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/accounts/${accountCode}/users/${userId}`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(userData)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // 서버 에러 응답 구조에 맞게 에러 메시지 처리
      if (errorData.errors && errorData.errors.error && Array.isArray(errorData.errors.error)) {
        const errorMessages = errorData.errors.error.map((err: any) => err.message).join(', ');
        throw new Error(errorMessages);
      }
      
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('사용자 수정 API 응답:', result);
    
    return {
      success: true,
      message: result.message || '사용자가 성공적으로 수정되었습니다.',
      data: result.data || result
    };
  } catch (error: any) {
    console.error('사용자 수정 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 사용자 삭제 API
export const deleteUser = async (
  accountCode: string,
  userId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/accounts/${accountCode}/users/${userId}/delete`),
      {
        method: 'POST',
        headers
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    return {
      success: true,
      message: '사용자가 성공적으로 삭제되었습니다.'
    };
  } catch (error: any) {
    console.error('사용자 삭제 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};